import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAppSettings } from "../hooks/app-settings";
import { AppSetting } from "../models";

interface Props {
  children: JSX.Element;
}

interface AppSettingContextValue {
  settings: AppSetting[] | null;
  isLoading: boolean;
  isError: boolean;
  ready: boolean;
  getSetting: (name: string) => AppSetting | undefined;
}

const AppSettingContext = React.createContext<AppSettingContextValue>({
  settings: null,
  isLoading: true,
  isError: false,
  ready: false,
  getSetting: () => undefined,
});

export const useAppSettingsContext = () => useContext(AppSettingContext);

const hydrateFromLocalStorage = (): AppSetting[] | null => {
  try {
    const raw = localStorage.getItem("appSettings");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AppSetting[]) : null;
  } catch {
    return null;
  }
};

export const AppSettingProvider = ({ children }: Props) => {
  const { data, isLoading, isError } = useAppSettings();
  const [settings, setSettings] = useState<AppSetting[] | null>(
    hydrateFromLocalStorage()
  );

  useEffect(() => {
    if (Array.isArray(data)) {
      setSettings(data);
      try {
        localStorage.setItem("appSettings", JSON.stringify(data));
      } catch {
        // ignore storage failures
      }
    } else if (data === undefined) {
      // do not write "undefined" into storage; leave as-is
    } else {
      try {
        localStorage.removeItem("appSettings");
      } catch {
        // ignore storage failures
      }
    }
  }, [data]);

  const getSetting = useCallback(
    (name: string): AppSetting | undefined =>
      settings?.find((appSetting) => appSetting.name === name),
    [settings]
  );

  const value = useMemo(
    () => ({
      settings,
      isLoading,
      isError,
      ready: Array.isArray(settings) && settings.length > 0 && !isLoading && !isError,
      getSetting,
    }),
    [settings, isLoading, isError, getSetting]
  );

  return (
    <AppSettingContext.Provider value={value}>
      {children}
    </AppSettingContext.Provider>
  );
};
